/**
 * Finishing and reopening a task, and remembering which stone this browser
 * placed.
 *
 * A stone can arrive on Today's cairn from another device through sync. It
 * falls either way, but only a stone placed here offers "Stone placed" with
 * Undo, as on the phone: a notice for work finished elsewhere would be one
 * nobody asked for.
 */

import { focusChanges, taskFocus } from '../../shared/focus';
import type { LocalRecord, Task } from '../../shared/model';

import { focusRuntime } from '../components/FocusHistory';
import { workspace } from './engine';

let last: { id: string; at: number } | undefined;

/** Whether `id` was finished in this browser in the last few seconds. */
export function placedHere(id: string, within = 10_000) {
  return last?.id === id && Date.now() - last.at < within;
}

/**
 * The pebble's decision, the same as Complete on the session panel: finishing
 * a running task ends its session. Reopening brings the task back but does not
 * revive the session.
 */
export async function setTaskDone(
  record: Pick<LocalRecord, 'id'>,
  done: boolean,
  deviceId: string,
) {
  const snapshot = workspace.snapshot();
  const current = snapshot.records.find(r => r.id === record.id);

  if (!current) {
    return;
  }

  if (!done) {
    await workspace.save(current.id, 'task', { ...(current.value as Task), done: false });

    return;
  }

  last = { id: current.id, at: Date.now() };

  await workspace.saveMany(
    focusChanges(
      snapshot.records,
      current.id,
      // The session's own device, so a session opened on the phone is ended too.
      taskFocus(snapshot.records, current.id, deviceId).deviceId,
      focusRuntime,
      'complete',
      '',
      Date.now(),
      !navigator.onLine || snapshot.offline,
    ),
  );
}
